import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtUsd, fmtTimeAgo } from "@/lib/nexus";
import { useAuth } from "@/context/AuthContext";
import { Boxes, Loader2, DownloadCloud, ArrowDownLeft, ArrowUpRight, ExternalLink, Activity } from "lucide-react";

const CHAINS = ["ethereum", "base", "polygon", "arbitrum", "optimism"];
const EXAMPLES = [
  { label: "vitalik.eth", addr: "0xd8dA6BF26964aF9D7eEd9e03E53415D37aA96045" },
  { label: "Uniswap Router", addr: "0x66a9893cC07D91D95644AEDD05D03f95e1dBA8Af" },
];

export default function OnChain() {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [address, setAddress] = useState("");
  const [chain, setChain] = useState("ethereum");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [error, setError] = useState("");
  const [ingestMsg, setIngestMsg] = useState("");

  const fetchWallet = async (e) => {
    e?.preventDefault();
    if (!address.trim()) return;
    setLoading(true); setError(""); setData(null); setIngestMsg("");
    try {
      const r = await api.post("/onchain/wallet", { address: address.trim(), chain });
      setData(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to fetch wallet");
    } finally {
      setLoading(false);
    }
  };

  const ingest = async () => {
    setIngesting(true); setIngestMsg("");
    try {
      const r = await api.post("/onchain/ingest", { address: address.trim(), chain });
      setIngestMsg(`Ingested via ${r.data.provider}: +${r.data.agents_added} agents, +${r.data.transactions_added} transactions merged into the graph.`);
    } catch (err) {
      setIngestMsg(err?.response?.data?.detail || "Ingest failed");
    } finally {
      setIngesting(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div>
        <div className="panel-title">ON-CHAIN INTEL // ETHERSCAN V2 · BLOCKSCOUT</div>
        <h1 className="font-rajdhani font-bold text-2xl tracking-tight mt-1">
          Ingest <span className="text-nexus-cyan">live wallet activity</span>
        </h1>
        <p className="text-[13px] text-nexus-text-2 mt-1 max-w-2xl">
          Look up any EVM wallet across 5 chains using real on-chain data, then overlay it onto the
          agentic-commerce graph, map, and transaction views.
        </p>
      </div>

      <form onSubmit={fetchWallet} className="panel brackets p-4 space-y-3">
        <span className="b1" /><span className="b2" />
        <div className="flex flex-col md:flex-row gap-3">
          <label className="flex-1 block">
            <span className="panel-title">Wallet address</span>
            <input data-testid="onchain-address-input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="0x…"
              className="mt-1 w-full bg-nexus-bg border border-nexus-border px-3 py-2 text-[13px] font-mono focus:border-nexus-cyan/60 outline-none" />
          </label>
          <label className="block md:w-56">
            <span className="panel-title">Chain</span>
            <select data-testid="onchain-chain-select" value={chain} onChange={(e) => setChain(e.target.value)}
              className="mt-1 w-full bg-nexus-bg border border-nexus-border px-2 py-2 text-[13px] font-mono uppercase">
              {CHAINS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-[10px] font-mono text-nexus-text-3">
            try:
            {EXAMPLES.map((ex) => (
              <button key={ex.addr} type="button" onClick={() => setAddress(ex.addr)}
                className="border border-nexus-border px-2 py-1 hover:border-nexus-cyan/60 hover:text-nexus-cyan">{ex.label}</button>
            ))}
          </div>
          <button data-testid="onchain-fetch-button" type="submit" disabled={loading}
            className="text-[10px] font-mono uppercase tracking-widest text-nexus-cyan border border-nexus-cyan/50 hover:bg-nexus-cyan/10 px-4 py-2 disabled:opacity-50 flex items-center gap-1">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Activity size={12} />} Fetch On-Chain
          </button>
        </div>
      </form>

      {error && <div data-testid="onchain-error" className="text-[12px] font-mono text-nexus-rose border border-nexus-rose/40 bg-nexus-rose/5 px-3 py-2">{error}</div>}

      {data && (
        <div className="space-y-3" data-testid="onchain-results">
          <div className="panel brackets p-4">
            <span className="b1" /><span className="b2" />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <div className="font-rajdhani font-bold text-lg tracking-widest uppercase">{data.chain_display}</div>
                  <span className={`text-[9px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${data.provider === "etherscan" ? "border-nexus-cyan/50 text-nexus-cyan" : data.provider === "blockscout" ? "border-nexus-amber/50 text-nexus-amber" : "border-nexus-rose/50 text-nexus-rose"}`}>
                    source: {data.provider}
                  </span>
                  <a href={`${data.explorer}/address/${data.address}`} target="_blank" rel="noreferrer" className="text-nexus-text-3 hover:text-nexus-cyan"><ExternalLink size={13} /></a>
                </div>
                <div className="hash mt-1">{data.address}</div>
              </div>
              <div className="flex gap-6">
                <div>
                  <div className="panel-title">Balance</div>
                  <div className="font-mono text-[15px] text-nexus-cyan">{data.balance_native} {data.symbol}</div>
                  <div className="text-[10px] font-mono text-nexus-text-3">≈ {fmtUsd(data.balance_usd)} (est.)</div>
                </div>
                <div>
                  <div className="panel-title">Txns</div>
                  <div className="font-mono text-[15px]">{data.tx_count}</div>
                </div>
              </div>
              {canWrite && (
                <button data-testid="onchain-ingest-button" onClick={ingest} disabled={ingesting}
                  className="text-[10px] font-mono uppercase tracking-widest text-nexus-violet border border-nexus-violet/50 hover:bg-nexus-violet/10 px-4 py-2 disabled:opacity-50 flex items-center gap-1">
                  {ingesting ? <Loader2 size={12} className="animate-spin" /> : <DownloadCloud size={12} />} Overlay onto Graph
                </button>
              )}
            </div>
            {ingestMsg && (
              <div data-testid="onchain-ingest-msg" className="mt-3 text-[11px] font-mono text-nexus-violet border border-nexus-violet/30 bg-nexus-violet/5 px-3 py-2 flex items-center justify-between gap-3">
                <span>{ingestMsg}</span>
                <button onClick={() => navigate("/graph")} className="text-nexus-cyan hover:underline shrink-0">View graph →</button>
              </div>
            )}
          </div>

          <div className="panel p-0 overflow-hidden">
            <div className="panel-title p-3 border-b border-nexus-border"><Boxes size={12} className="inline mr-1" /> Recent transactions ({data.transactions.length})</div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px] font-mono">
                <thead>
                  <tr className="text-nexus-text-3 text-[10px] uppercase tracking-widest border-b border-nexus-border">
                    <th className="text-left px-3 py-2">Dir</th>
                    <th className="text-left px-3 py-2">Hash</th>
                    <th className="text-left px-3 py-2">Kind</th>
                    <th className="text-right px-3 py-2">Value</th>
                    <th className="text-left px-3 py-2 pl-4">Counterparty</th>
                    <th className="text-right px-3 py-2">Age</th>
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.map((t) => (
                    <tr key={t.hash + t.timestamp + t.value_native} className="border-b border-nexus-border/40 hover:bg-nexus-panel-2/40">
                      <td className="px-3 py-2">
                        {t.direction === "out"
                          ? <span className="flex items-center gap-1 text-nexus-rose"><ArrowUpRight size={12} /> OUT</span>
                          : <span className="flex items-center gap-1 text-nexus-cyan"><ArrowDownLeft size={12} /> IN</span>}
                      </td>
                      <td className="px-3 py-2 text-nexus-text-2">{t.hash?.slice(0, 12)}…</td>
                      <td className="px-3 py-2"><span className="text-[9px] uppercase tracking-widest border border-nexus-border px-1">{t.kind}</span></td>
                      <td className="px-3 py-2 text-right">{Number(t.value_native).toLocaleString(undefined, { maximumFractionDigits: 4 })} {t.symbol}</td>
                      <td className="px-3 py-2 pl-4 text-nexus-text-3">{(t.direction === "out" ? t.to : t.from)?.slice(0, 10)}…</td>
                      <td className="px-3 py-2 text-right text-nexus-text-3">{fmtTimeAgo(t.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
